import { useEffect, useRef } from 'react';
import useMediaQuery from '../utils/useMediaQuery';
import StoryVideoPlayer from './media/StoryVideoPlayer';
import usePageScrollLock from '../utils/usePageScrollLock';
import useSwipeToDismiss from '../hooks/useSwipeToDismiss';

/**
 * MediaViewer — the full media experience for an event's gallery.
 *
 *  • Mobile: an Instagram-Story-style full-screen viewer. Tap the right side to advance,
 *    the left to go back; it loops at the ends. A segmented progress bar shows position.
 *  • Desktop: a lightbox with the media centered, prev/next arrows, keyboard nav, and a
 *    thumbnail strip along the bottom.
 *  • Videos play in the story video player (`components/media/StoryVideoPlayer`, messages
 *    handoff Part D): edge taps change items, the middle halves skip on the tap ramp, the
 *    bottom edge reveals the scrub bar with its thumbnail preview; on desktop the bar also
 *    shows on hover and J / L seek. Embeds fall back to the provider's iframe.
 *
 * Props: items[], index, onIndex(i), onClose()
 */
export default function MediaViewer({ items = [], index = 0, onIndex, onClose, title = '' }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return null;
  const safe = Math.max(0, Math.min(index, list.length - 1));

  const go = (dir) => onIndex((safe + dir + list.length) % list.length); // wraps (story loop)

  return isDesktop
    ? <Lightbox items={list} index={safe} go={go} onIndex={onIndex} onClose={onClose} />
    : <Story items={list} index={safe} go={go} onClose={onClose} title={title} />;
}

/* ─────────────────────────── Mobile: Instagram-Story ─────────────────────────── */

function Story({ items, index, go, onClose, title = '' }) {
  const item = items[index];

  // Lock body scroll while the story is open.
  usePageScrollLock(true);
  // Swipe it down to close, like the portal viewer.
  const frameRef = useRef(null);
  const swipe = useSwipeToDismiss(frameRef, { onDismiss: () => onClose?.() });

  return (
    <div ref={frameRef} data-testid="media-viewer-story" className="fixed inset-0 z-[80] flex flex-col bg-black" style={{ height: '100dvh', ...swipe.style }}>
      {/* Segmented progress (one per item). */}
      <div className="flex gap-1 px-2 pt-2">
        {items.map((_, i) => (
          <span key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/30">
            <span className={`block h-full rounded-full bg-white transition-all ${i < index ? 'w-full' : i === index ? 'w-full' : 'w-0'}`} />
          </span>
        ))}
      </div>

      <div className="flex items-center gap-2 px-3 py-2">
        <span className="flex min-w-0 flex-1 items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-white">
          <span aria-hidden>◍</span>
          <span className="font-bold">Portal</span>
          {title && (
            <>
              <span className="text-white/50" aria-hidden>·</span>
              <span className="truncate text-white/80">{title}</span>
            </>
          )}
        </span>
        <span className="shrink-0 font-mono text-[11px] uppercase tracking-wider text-white/60">{index + 1} / {items.length}</span>
        <button type="button" onClick={onClose} aria-label="Close"
          className="flex h-8 w-8 shrink-0 items-center justify-center text-2xl leading-none text-white">×</button>
      </div>

      {/* Stage. Photo: left third = back, the rest = forward. An uploaded video hands its taps
          to the player (edges = items, middle = skip ramp, hold = pause). */}
      <div className="relative flex-1 select-none overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          {/* key by index so each item (esp. a video) remounts and autoplays on prev/next. */}
          <MediaFrame key={index} item={item} active minimal onPrev={() => go(-1)} onNext={() => go(1)} />
        </div>
        {/* The stage IS these two tap zones, so they carry `data-swipe-ok` — without it the
            swipe-down would read every touch as a button's and never engage. A drag that was
            a dismiss (closed, or sprung back) is never also a page turn. */}
        {!(item.kind === 'video' && item.hlsUrl) && (
          <>
            <button type="button" aria-label="Previous" data-swipe-ok onClick={() => { if (!swipe.claimed.current) go(-1); }}
              className="absolute left-0 top-0 bottom-0 w-1/3" />
            <button type="button" aria-label="Next" data-swipe-ok onClick={() => { if (!swipe.claimed.current) go(1); }}
              className="absolute right-0 top-0 bottom-0 w-2/3" />
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────── Desktop: Lightbox ─────────────────────────────── */

function Lightbox({ items, index, go, onIndex, onClose }) {
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/95" onClick={onClose}>
      <div className="flex items-center justify-between p-3">
        <span className="font-mono text-xs uppercase tracking-wider text-white/70">{index + 1} / {items.length}</span>
        <button type="button" onClick={onClose}
          className="border border-white px-3 py-1 font-mono text-xs uppercase tracking-wider text-white hover:bg-white hover:text-black">Close</button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center gap-2 px-2" onClick={(e) => e.stopPropagation()}>
        {items.length > 1 && (
          <button type="button" onClick={() => go(-1)} aria-label="Previous"
            className="shrink-0 px-2 font-mono text-4xl text-white/60 hover:text-white">‹</button>
        )}
        <div className="flex min-w-0 flex-1 items-center justify-center">
          <div className="max-h-full w-full max-w-5xl">
            <MediaFrame key={index} item={item} active onPrev={items.length > 1 ? () => go(-1) : null} onNext={items.length > 1 ? () => go(1) : null} />
          </div>
        </div>
        {items.length > 1 && (
          <button type="button" onClick={() => go(1)} aria-label="Next"
            className="shrink-0 px-2 font-mono text-4xl text-white/60 hover:text-white">›</button>
        )}
      </div>

      {/* Thumbnail strip. */}
      {items.length > 1 && (
        <div className="flex justify-center gap-2 overflow-x-auto p-3" onClick={(e) => e.stopPropagation()}>
          {items.map((it, i) => {
            const thumb = it.thumbnailUrl || it.url;
            const isVideo = it.kind === 'embed' || it.kind === 'video';
            return (
              <button key={i} type="button" onClick={() => onIndex(i)}
                className={`relative h-14 w-20 shrink-0 overflow-hidden border-2 ${i === index ? 'border-white' : 'border-white/30 opacity-60 hover:opacity-100'}`}>
                {thumb
                  ? <img src={thumb} alt="" className="h-full w-full object-cover" />
                  : <span className="flex h-full w-full items-center justify-center bg-black font-mono text-[9px] text-white/60">MEDIA</span>}
                {isVideo && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">▶</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────── Shared bits ──────────────────────────────── */

function MediaFrame({ item, active, minimal = false, onPrev = null, onNext = null }) {
  if (item.kind === 'image') {
    return <img src={item.url} alt="" className="mx-auto max-h-[85dvh] w-auto max-w-full object-contain" />;
  }
  return <GalleryVideo item={item} active={active} minimal={minimal} onPrev={onPrev} onNext={onNext} />;
}

/**
 * GalleryVideo — an uploaded clip plays in the story video player (the one expanded player
 * in the product); embeds fall back to a privacy-mode provider iframe.
 */
function GalleryVideo({ item, active, minimal = false, onPrev = null, onNext = null }) {
  const isUpload = item.kind === 'video' && !!item.hlsUrl;
  const clip = isUpload ? { id: item.id, kind: 'video', mediaUrl: item.hlsUrl, thumbnailUrl: item.thumbnailUrl, durationSec: item.durationSec, width: item.width, height: item.height, storyboardUrl: item.storyboardUrl } : null;

  if (isUpload && minimal) {
    // Full-bleed on a phone: the player owns the frame, its taps and the scrub bar.
    return (
      <div className="absolute inset-0">
        <StoryVideoPlayer item={clip} active={active} muted={false} onPrev={onPrev} onNext={onNext} onEnded={onNext || undefined} bottomInset={12} />
      </div>
    );
  }

  if (isUpload) {
    // Desktop lightbox: a 16:9 stage; the bar shows on hover as well as on touch, J / L seek.
    return (
      <div className="relative mx-auto w-full max-w-full bg-black" style={{ aspectRatio: '16 / 9', maxHeight: '80dvh' }}>
        <StoryVideoPlayer item={clip} active={active} muted={false} onPrev={onPrev} onNext={onNext} hoverBar bottomInset={12} />
        <span className="pointer-events-none absolute left-2 top-2 z-20 flex items-center gap-1 bg-black/70 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
          <span aria-hidden>◍</span> Portal
        </span>
      </div>
    );
  }

  // Embed (YouTube/Vimeo) — provider iframe inside our frame.
  const src = buildEmbedSrc(item);
  return (
    <div className="relative mx-auto w-full max-w-5xl overflow-hidden border-2 border-white bg-black" style={{ aspectRatio: '16 / 9' }}>
      {src ? (
        <iframe title="Event video" src={src} className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen referrerPolicy="strict-origin" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center font-mono text-xs text-white/70">Video is still processing…</div>
      )}
      <span className="pointer-events-none absolute left-2 top-2 flex items-center gap-1 bg-black/70 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
        <span aria-hidden>◍</span> Portal
      </span>
    </div>
  );
}

function buildEmbedSrc(item) {
  if (item.embedProvider === 'youtube' && item.embedId)
    return `https://www.youtube-nocookie.com/embed/${item.embedId}?rel=0&modestbranding=1&playsinline=1`;
  if (item.embedProvider === 'vimeo' && item.embedId)
    return `https://player.vimeo.com/video/${item.embedId}?byline=0&portrait=0&title=0`;
  return item.embedUrl || null;
}
